/**
 * Modal component - Reusable dialog overlay
 * Mobile-first con gestos y accesibilidad
 */

import { useEffect, useCallback, useId, useRef } from 'react';
import type { ReactNode, MouseEvent, CSSProperties, RefObject } from 'react';
import { cva } from 'class-variance-authority';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { cn } from '../../../utils';
import { useVisualViewport } from '../../../hooks/useVisualViewport';

interface ModalProps {
    /** Si el modal está abierto */
    isOpen: boolean;
    /** Callback al cerrar */
    onClose: () => void;
    /** Título del modal */
    title?: ReactNode;
    /** Contenido del modal */
    children: ReactNode;
    /** Si se puede cerrar haciendo clic fuera */
    closeOnBackdrop?: boolean;
    /** Si se puede cerrar con Escape */
    closeOnEscape?: boolean;
    /** Tamaño del modal */
    size?: 'small' | 'medium' | 'large';
    /** Clases CSS adicionales */
    className?: string;
    /** Elemento que debe recibir foco al abrir; por defecto se enfoca el diálogo */
    initialFocusRef?: RefObject<HTMLElement | null>;
    /** Id de la descripción accesible del diálogo */
    ariaDescribedBy?: string;
    /** Impide cerrar el diálogo mientras una acción crítica está en curso */
    closeDisabled?: boolean;
}

const modalSizeVariants = cva(
    'w-full max-h-[calc(100dvh-2rem)] overflow-hidden rounded-2xl bg-surface shadow-lg',
    {
        variants: {
            size: {
                small: 'max-w-sm',
                medium: 'max-w-md',
                large: 'max-w-xl',
            },
        },
        defaultVariants: {
            size: 'medium',
        },
    }
);

export function Modal({
    isOpen,
    onClose,
    title,
    children,
    closeOnBackdrop = true,
    closeOnEscape = true,
    size = 'medium',
    className = '',
    initialFocusRef,
    ariaDescribedBy,
    closeDisabled = false,
}: ModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const onCloseRef = useRef(onClose);
    const closeOnEscapeRef = useRef(closeOnEscape);
    const viewport = useVisualViewport();
    const titleId = useId();

    useEffect(() => {
        onCloseRef.current = onClose;
        closeOnEscapeRef.current = closeOnEscape && !closeDisabled;
    }, [closeDisabled, closeOnEscape, onClose]);

    // Manejar tecla Escape
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (closeOnEscapeRef.current && e.key === 'Escape') {
                onCloseRef.current();
                return;
            }

            if (e.key !== 'Tab') {
                return;
            }

            const modalNode = modalRef.current;
            if (!modalNode) {
                return;
            }

            const focusableElements = Array.from(
                modalNode.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )
            ).filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');

            if (focusableElements.length === 0) {
                e.preventDefault();
                modalNode.focus();
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;

            if (e.shiftKey && activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
                return;
            }

            if (!e.shiftKey && activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        },
        []
    );

    // Click en backdrop
    const handleBackdropClick = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            if (closeOnBackdrop && !closeDisabled && e.target === e.currentTarget) {
                onClose();
            }
        },
        [closeDisabled, closeOnBackdrop, onClose]
    );

    // Gestionar listeners globales mientras el modal está abierto
    useEffect(() => {
        if (isOpen) {
            previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';

            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
                previouslyFocusedRef.current?.focus();
                previouslyFocusedRef.current = null;
            };
        }
    }, [isOpen, handleKeyDown]);

    // Enfocar el modal solo al abrir (evita robar foco en cada re-render).
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        (initialFocusRef?.current ?? modalRef.current)?.focus();
    }, [initialFocusRef, isOpen]);

    if (!isOpen) return null;

    // Anclar el overlay al visual viewport para que el teclado virtual
    // empuje el contenido en lugar de taparlo.
    const overlayStyle: CSSProperties = {
        top: viewport.offsetTop,
        height: viewport.height,
    };

    return (
        <div
            className="animate-fade-in fixed left-0 right-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
            style={overlayStyle}
            onClick={handleBackdropClick}
            role="presentation"
        >
            <div
                ref={modalRef}
                className={cn(
                    modalSizeVariants({ size }),
                    'animate-slide-up flex flex-col',
                    className
                )}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-describedby={ariaDescribedBy}
                tabIndex={-1}
            >
                {title && (
                    <header className="flex items-center justify-between border-b border-border px-5 py-4">
                        <h2 id={titleId} className="text-lg font-semibold text-text">
                            {title}
                        </h2>
                        <button
                            type="button"
                            className="inline-flex min-h-12 min-w-12 items-center justify-center rounded-lg text-text-secondary transition-[background-color,color,opacity] hover:bg-hover hover:text-text disabled:cursor-wait disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
                            onClick={onClose}
                            aria-label="Cerrar"
                            disabled={closeDisabled}
                        >
                            <FontAwesomeIcon icon={faXmark} className="text-xl" aria-hidden="true" />
                        </button>
                    </header>
                )}
                <div className="overflow-y-auto overscroll-contain px-5 pb-[calc(1.25rem+var(--spacing-safe-bottom,0px))] pt-5">
                    {children}
                </div>
            </div>
        </div>
    );
}
